/**
 * @file Composant de vérification 2FA affiché lors de la connexion.
 * Lorsqu'un utilisateur dont la 2FA est activée se connecte, le backend renvoie
 * `{ requires_2fa: true }`. Ce composant permet à l'utilisateur de fournir
 * soit un code TOTP à 6 chiffres, soit un code de récupération à usage unique.
 *
 * Endpoint consommé :
 * - POST /auth/2fa/verify/ → { code } ou { recovery_code }
 *
 * @see R-0035
 */

import { useState } from 'react';
import api from '../lib/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Loader2, ShieldCheck, KeyRound } from 'lucide-react';

/**
 * Composant de défi 2FA affiché pendant le flux de connexion.
 * Propose deux modes : saisie d'un code TOTP ou saisie d'un code de récupération.
 * Appelle `onSuccess()` lorsque la vérification réussit.
 *
 * @param {Object} props
 * @param {Function} props.onSuccess - Callback invoqué après vérification réussie du code 2FA.
 * @returns {JSX.Element}
 */
export default function TwoFactorChallenge({ onSuccess }) {
  /* ── Mode courant : 'totp' (code à 6 chiffres) ou 'recovery' (code de récupération) ── */
  const [mode, setMode] = useState('totp');

  /* ── Valeurs des champs ── */
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');

  /* ── État de chargement et erreurs ── */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Envoie le code de vérification (TOTP ou récupération) au backend.
   * En mode TOTP, envoie `{ code }`. En mode récupération, envoie `{ recovery_code }`.
   * En cas de succès, invoque le callback `onSuccess`.
   * @param {React.FormEvent} e - Événement de soumission du formulaire.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = mode === 'totp'
        ? { code: totpCode }
        : { recovery_code: recoveryCode };
      await api.post('/auth/2fa/verify/', payload);
      onSuccess();
    } catch (e) {
      setError(
        e.response?.data?.error ||
        (mode === 'totp' ? 'Invalid code, please try again' : 'Invalid recovery code')
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Bascule entre le mode TOTP et le mode récupération.
   * Réinitialise les erreurs et les champs à chaque changement de mode.
   * @param {'totp'|'recovery'} newMode - Nouveau mode à activer.
   */
  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setTotpCode('');
    setRecoveryCode('');
  };

  return (
    <div className="w-full max-w-sm px-6" data-testid="2fa-challenge">
      <h1
        className="text-2xl sm:text-3xl font-medium tracking-tight text-foreground mb-1"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        Two-factor verification
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        {mode === 'totp'
          ? 'Enter the 6-digit code from your authenticator app'
          : 'Enter one of your recovery codes'}
      </p>

      {/* ── Onglets de sélection du mode ── */}
      <div className="flex gap-1 mb-6 p-1 bg-muted rounded-sm" data-testid="2fa-mode-tabs">
        <button
          type="button"
          onClick={() => switchMode('totp')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-sm transition-colors ${
            mode === 'totp'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="2fa-tab-totp"
        >
          <ShieldCheck className="h-3.5 w-3.5" />
          Authenticator
        </button>
        <button
          type="button"
          onClick={() => switchMode('recovery')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-sm transition-colors ${
            mode === 'recovery'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="2fa-tab-recovery"
        >
          <KeyRound className="h-3.5 w-3.5" />
          Recovery code
        </button>
      </div>

      {/* ── Message d'erreur ── */}
      {error && (
        <div className="mb-4 p-3 text-sm text-red-600 border border-red-200 rounded-sm bg-red-50" data-testid="2fa-challenge-error">
          {error}
        </div>
      )}

      {/* ── Formulaire de vérification ── */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'totp' ? (
          <div>
            <Label htmlFor="totp-input" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Authentication code
            </Label>
            <Input
              id="totp-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring font-mono text-center text-lg tracking-widest"
              required
              autoComplete="one-time-code"
              data-testid="2fa-totp-input"
            />
          </div>
        ) : (
          <div>
            <Label htmlFor="recovery-input" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Recovery code
            </Label>
            <Input
              id="recovery-input"
              type="text"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              placeholder="xxxx-xxxx-xxxx"
              className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring font-mono text-center tracking-wider"
              required
              autoComplete="off"
              data-testid="2fa-recovery-input"
            />
          </div>
        )}

        <Button
          type="submit"
          className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
          disabled={loading || (mode === 'totp' ? totpCode.length !== 6 : !recoveryCode.trim())}
          data-testid="2fa-submit-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
        </Button>
      </form>
    </div>
  );
}
